import { useRef, useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Upload, X, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { storage } from "@/lib/firebase";

// Single & multi-image uploader to Firebase Storage (resumable, with progress),
// themed for A Homely Abode. Mirrors the MeProperty uploader: reorder, set
// primary, remove (best-effort Storage delete).
//   path     — Storage folder, e.g. "media/listings/abc123"
//   value    — string[] of image URLs (controlled)
//   onChange — (urls) => void
//   max      — max images (default 10)
//   single   — if true, value holds a single URL (array under the hood)

function fileExt(name = "") {
  const m = name.match(/\.[a-z0-9]+$/i);
  return m ? m[0].toLowerCase() : "";
}

export default function ImageUploader({
  path,
  value = [],
  onChange,
  max = 10,
  single = false,
  testid = "image-uploader",
}) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState({}); // { fileName: 0..100 }
  const [error, setError] = useState("");

  const images = Array.isArray(value) ? value : value ? [value] : [];

  const handleFiles = async (files) => {
    setError("");
    const list = Array.from(files || []);
    if (list.length === 0) return;

    const remaining = single ? 1 : max - images.length;
    const accepted = list.slice(0, Math.max(0, remaining));
    if (accepted.length < list.length) setError(`Only ${remaining} more image(s) allowed (max ${max}).`);

    const uploaded = [];
    await Promise.all(
      accepted.map((file) => new Promise((resolve) => {
        if (!file.type.startsWith("image/")) {
          setError(`"${file.name}" is not an image and was skipped.`);
          return resolve();
        }
        const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const key = `${path}/${stamp}${fileExt(file.name)}`;
        const task = uploadBytesResumable(ref(storage, key), file, { contentType: file.type });
        task.on(
          "state_changed",
          (snap) => setProgress((p) => ({ ...p, [file.name]: (snap.bytesTransferred / snap.totalBytes) * 100 })),
          (err) => {
            // eslint-disable-next-line no-console
            console.error("Upload failed", err);
            setError(`Upload failed for ${file.name}: ${err.code || err.message}`);
            setProgress((p) => { const { [file.name]: _, ...rest } = p; return rest; });
            resolve();
          },
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            uploaded.push(url);
            setProgress((p) => { const { [file.name]: _, ...rest } = p; return rest; });
            resolve();
          }
        );
      }))
    );

    if (uploaded.length > 0) onChange(single ? [uploaded[0]] : [...images, ...uploaded]);
  };

  const removeAt = async (i) => {
    const url = images[i];
    onChange(images.filter((_, j) => j !== i));
    try { await deleteObject(ref(storage, url)); } catch { /* best-effort */ }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const makePrimary = (i) => {
    if (i === 0) return;
    onChange([images[i], ...images.filter((_, j) => j !== i)]);
  };

  const canAdd = single ? images.length === 0 : images.length < max;
  const uploading = Object.keys(progress).length > 0;

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        {images.map((url, i) => (
          <div key={url + i} className="relative group overflow-hidden bg-beige border border-bronze/20">
            <img src={url} alt="" className="w-full h-28 object-cover" />
            {!single && i === 0 && (
              <span className="absolute top-2 left-2 px-2 py-0.5 bg-bronze text-cream text-[10px] font-semibold uppercase tracking-widest">
                Primary
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 p-1.5 flex items-center justify-between bg-gradient-to-t from-ink/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              {!single && (
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move left" className="p-1.5 bg-cream/90 text-ink hover:bg-cream disabled:opacity-30"><ArrowLeft size={12} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} title="Move right" className="p-1.5 bg-cream/90 text-ink hover:bg-cream disabled:opacity-30"><ArrowRight size={12} /></button>
                  {i !== 0 && (
                    <button type="button" onClick={() => makePrimary(i)} title="Set as primary" className="p-1.5 bg-cream/90 text-bronze hover:bg-cream"><Star size={12} /></button>
                  )}
                </div>
              )}
              <button type="button" onClick={() => removeAt(i)} title="Remove" data-testid={`${testid}-remove-${i}`} className="p-1.5 bg-cream/90 text-red-600 hover:bg-cream ml-auto"><X size={12} /></button>
            </div>
          </div>
        ))}

        {canAdd && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-add`}
            className="h-28 border-2 border-dashed border-bronze/30 hover:border-bronze hover:bg-beige/40 flex flex-col items-center justify-center text-olive transition-colors disabled:opacity-50"
          >
            <Upload size={18} className="mb-1.5" />
            <span className="text-xs font-medium">{uploading ? "Uploading…" : "Add image"}</span>
            {!single && <span className="text-[10px] text-olive/70 mt-0.5">{images.length}/{max}</span>}
          </button>
        )}
      </div>

      {uploading && (
        <div className="space-y-1.5 mb-2">
          {Object.entries(progress).map(([name, pct]) => (
            <div key={name} className="text-xs text-olive">
              <div className="flex justify-between mb-0.5"><span className="truncate">{name}</span><span>{Math.round(pct)}%</span></div>
              <div className="h-1 bg-bronze/15 overflow-hidden"><div className="h-full bg-bronze" style={{ width: pct + "%" }} /></div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-2 mb-2">{error}</div>}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={!single}
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
